import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api, IS_NATIVE_APP } from '../api';

// ─────────────────────────────────────────────────────────────────
// Pushnotiser i iOS- och Android-appen (@capacitor/push-notifications). Appen frågar inte
// om lov vid start: PushPrompt i App.tsx erbjuder det när man är inloggad,
// och först när användaren trycker "Slå på" visar iOS sin fråga. Ett tryck
// på en notis öppnar djuplänken (samma som i mejlet, t.ex. /?tab=meddelanden).
// På webben gör inget här något.
// ─────────────────────────────────────────────────────────────────

const TOKEN_KEY = 'qvestium_push_token';
const DISMISSED_KEY = 'qvestium_push_prompt_dismissed';

export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unavailable';

const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* privat läge */ } },
  del: (k: string) => { try { localStorage.removeItem(k); } catch { /* privat läge */ } },
};

// Android-bygget utan google-services.json har ingen Firebase-app, och då
// kraschar PushNotifications.register(). PushConfigPlugin.java svarar på om
// push finns i bygget.
const PushConfig = registerPlugin<{ isAvailable(): Promise<{ available: boolean }> }>('PushConfig');
let configured: Promise<boolean> | null = null;
export function pushConfigured(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'android') return Promise.resolve(true);
  configured ??= PushConfig.isAvailable().then(r => r.available, () => false);
  return configured;
}

export async function pushPermission(): Promise<PushPermission> {
  if (!IS_NATIVE_APP || !(await pushConfigured())) return 'unavailable';
  try {
    const { receive } = await PushNotifications.checkPermissions();
    return receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'prompt';
  } catch {
    return 'unavailable';
  }
}

let listening = false;

/** Lyssnare för token och tryck på notiser. Anropas en gång när appen startar. */
export function initPush(): void {
  if (!IS_NATIVE_APP || listening) return;
  listening = true;
  PushNotifications.addListener('registration', ({ value }) => {
    store.set(TOKEN_KEY, value);
    api.registerPushToken(value).catch(() => { /* försöks igen vid nästa start */ });
  });
  PushNotifications.addListener('registrationError', err => console.warn('[push] registrering misslyckades', err));
  PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const path = (notification.data as { path?: unknown } | undefined)?.path;
    // Bara egna, relativa länkar.
    if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) window.location.href = path;
  });
}

/** Inloggad och redan godkänd: registrera (iOS ger samma token, servern kopplar den till rätt konto). */
export async function syncPush(): Promise<void> {
  if ((await pushPermission()) === 'granted') await PushNotifications.register();
}

/** Användaren tryckte "Slå på": iOS frågar, och vid ja registreras enheten. */
export async function enablePush(): Promise<boolean> {
  store.set(DISMISSED_KEY, '1');
  if (!(await pushConfigured())) return false;
  const { receive } = await PushNotifications.requestPermissions();
  if (receive !== 'granted') return false;
  await PushNotifications.register();
  return true;
}

export const pushPromptDismissed = () => store.get(DISMISSED_KEY) === '1';
export const dismissPushPrompt = () => store.set(DISMISSED_KEY, '1');

/** Utloggning: enheten ska inte få fler notiser för kontot. */
export async function unregisterPush(): Promise<void> {
  const token = store.get(TOKEN_KEY);
  if (!IS_NATIVE_APP || !token) return;
  store.del(TOKEN_KEY);
  await api.unregisterPushToken(token).catch(() => {});
}
